import { useColorMode } from '@chakra-ui/react';
import { FC, useEffect, useRef } from 'react';

import { AnalyticsEvent, track } from '@bonusly/analytics';
import {
	Box,
	Button,
	HStack,
	Modal,
	ModalBody,
	ModalCloseButton,
	ModalContent,
	ModalFooter,
	ModalHeader,
	ModalOverlay,
	Text,
	VStack,
} from '@bonusly/design-system';

import { getSeasonConfig } from './get-season-config';
import { ACTIVE_SEASON } from './seasonal-config';
import { SeasonConfig } from './types';

interface SeasonalThemeModalProps {
	isOpen: boolean;
	onClose: () => void;
	onEnable: () => void;
}

/**
 * Modal that displays when users click to enable the seasonal theme
 * Shows fun information about the current seasonal theme features
 * Automatically uses assets and colors from the active season configuration
 */
export const SeasonalThemeModal: FC<SeasonalThemeModalProps> = ({ isOpen, onClose, onEnable }) => {
	const audioRef = useRef<HTMLAudioElement | null>(null);
	const { colorMode } = useColorMode();
	const seasonConfig = getSeasonConfig();

	/** Light seasonal modal (spring) on a dark app shell — use semantic tokens instead of fixed light palette */
	const useSemanticDarkModal = seasonConfig.colorModeStrategy === 'time-based' && colorMode === 'dark';

	// Track whether the current open cycle ended via Enable, so that the wrapped
	// close handler can distinguish "dismissed without enabling" from "closed
	// because the user enabled the theme" (which already fires its own event).
	const didEnableRef = useRef(false);

	useEffect(() => {
		if (isOpen) {
			didEnableRef.current = false;
		}
	}, [isOpen]);

	useEffect(() => {
		if (isOpen && seasonConfig.modalSound) {
			audioRef.current = new Audio(seasonConfig.modalSound);
			audioRef.current.volume = seasonConfig.modalSoundVolume;
			audioRef.current.play().catch((error) => {
				console.warn('Failed to play seasonal sound:', error);
			});
		} else {
			if (audioRef.current) {
				audioRef.current.pause();
				audioRef.current.currentTime = 0;
				audioRef.current = null;
			}
		}

		return () => {
			if (audioRef.current) {
				audioRef.current.pause();
				audioRef.current = null;
			}
		};
	}, [isOpen, seasonConfig.modalSound, seasonConfig.modalSoundVolume]);

	const handleEnable = () => {
		didEnableRef.current = true;
		track(AnalyticsEvent.SeasonalTheme.Enabled, { season: ACTIVE_SEASON });
		onEnable();
		onClose();
	};

	const handleClose = () => {
		if (!didEnableRef.current) {
			track(AnalyticsEvent.SeasonalTheme.ModalDismissed, { season: ACTIVE_SEASON });
		}
		onClose();
	};

	const { modalVariant } = seasonConfig;

	return (
		<Modal isOpen={isOpen} onClose={handleClose} size="md" isCentered>
			<ModalOverlay bg={useSemanticDarkModal ? 'bg.overlayBackdrop' : seasonConfig.modalColors.overlayBg} backdropFilter="blur(4px)" />
			{modalVariant === 'scenic' ? (
				<ScenicModalContent seasonConfig={seasonConfig} useSemanticDarkModal={useSemanticDarkModal} onClose={handleClose} onEnable={handleEnable} />
			) : (
				<ClassicModalContent seasonConfig={seasonConfig} useSemanticDarkModal={useSemanticDarkModal} onClose={handleClose} onEnable={handleEnable} />
			)}
		</Modal>
	);
};

interface VariantProps {
	seasonConfig: SeasonConfig;
	useSemanticDarkModal: boolean;
	onClose: () => void;
	onEnable: () => void;
}

/**
 * Original layout preserved for fall/winter themes.
 */
const ClassicModalContent: FC<VariantProps> = ({ seasonConfig, useSemanticDarkModal, onClose, onEnable }) => {
	const { modalColors, modalDecorations, logoDark, logoLight, modalText } = seasonConfig;
	const modalLogo = useSemanticDarkModal ? logoDark : modalColors.logoOverride === 'light' ? logoLight : logoDark;

	return (
		<ModalContent
			bg={useSemanticDarkModal ? 'bg.surface' : modalColors.contentBg}
			bgImage={useSemanticDarkModal ? undefined : modalColors.contentBgImage}
			bgSize={useSemanticDarkModal ? undefined : modalColors.contentBgSize}
			bgPosition={useSemanticDarkModal ? undefined : modalColors.contentBgPosition}
			bgRepeat={useSemanticDarkModal ? undefined : modalColors.contentBgRepeat}
			borderRadius="xl"
			boxShadow={useSemanticDarkModal ? 'xl' : modalColors.boxShadow}
			border="1px solid"
			borderColor={useSemanticDarkModal ? 'border.default' : modalColors.borderColor}
			overflow="hidden"
		>
			{modalDecorations.map((decoration, index) => (
				<Box
					key={index}
					position="absolute"
					top={decoration.top}
					right={decoration.right}
					bottom={decoration.bottom}
					left={decoration.left}
					fontSize={decoration.fontSize}
					opacity={decoration.opacity}
					transform={`rotate(${decoration.rotation}deg)`}
				>
					{decoration.emoji}
				</Box>
			))}

			<ModalHeader pt={8} pb={4} px={6} position="relative" zIndex={1} borderBottomWidth={0}>
				{modalLogo && (
					<VStack spacing={3} align="center">
						<Box
							as="img"
							src={modalLogo}
							alt="Seasonal Bonusly Logo"
							maxW={modalColors.logoMaxWidth || '220px'}
							w="100%"
							h="auto"
							{...(useSemanticDarkModal || modalColors.logoDropShadowColor === 'transparent'
								? {}
								: { filter: `drop-shadow(0 4px 15px ${modalColors.logoDropShadowColor})` })}
						/>
					</VStack>
				)}
				<ModalCloseButton
					color={useSemanticDarkModal ? 'text.default' : modalColors.cancelButtonColor}
					_hover={{
						bg: useSemanticDarkModal ? 'secondary.bgAlphaHover' : modalColors.closeButtonHoverBg || 'whiteAlpha.200',
						color: useSemanticDarkModal ? 'text.default' : modalColors.cancelButtonHoverColor,
					}}
					top={4}
					right={4}
					size="lg"
				/>
			</ModalHeader>

			<ModalBody px={6} pb={4} position="relative" zIndex={1}>
				<VStack spacing={4} align="stretch">
					<Text
						fontSize="md"
						color={useSemanticDarkModal ? 'text.default' : modalColors.subtitleColor}
						textAlign="center"
						lineHeight="tall"
						fontWeight="medium"
						my={4}
					>
						{modalText.subtitle}
					</Text>

					<VStack spacing={3} align="stretch">
						{modalText.features.map((feature, index) => (
							<HStack
								key={index}
								spacing={3}
								align="start"
								bg={useSemanticDarkModal ? 'bg.subtle' : modalColors.featureCardBg || 'whiteAlpha.50'}
								p={2.5}
								borderRadius="md"
								border="1px solid"
								borderColor={useSemanticDarkModal ? 'border.soft' : modalColors.featureCardBorderColor || 'whiteAlpha.100'}
							>
								<Text fontSize="lg" flexShrink={0}>
									{feature.emoji}
								</Text>
								<Text fontSize="xs" color={useSemanticDarkModal ? 'text.default' : modalColors.featureCardTextColor || 'whiteAlpha.900'} lineHeight="tall">
									{feature.text}
								</Text>
							</HStack>
						))}
					</VStack>

					<Box
						bg={useSemanticDarkModal ? 'bg.soft' : modalColors.noticeBg}
						borderRadius="md"
						p={3}
						border="1px solid"
						borderColor={useSemanticDarkModal ? 'border.soft' : modalColors.noticeBorderColor}
						mt={1}
					>
						<Text fontSize="xs" color={useSemanticDarkModal ? 'text.soft' : modalColors.noticeTextColor} textAlign="center" fontStyle="italic">
							{modalText.endDateNotice}
						</Text>
					</Box>
				</VStack>
			</ModalBody>

			<ModalFooter px={6} pb={6} pt={3} position="relative" zIndex={1} flexDirection="column" gap={2}>
				<Button
					onClick={onEnable}
					size="md"
					width="full"
					bg={useSemanticDarkModal ? 'primary.bg' : modalColors.enableButtonBg}
					color={useSemanticDarkModal ? 'text.onColor' : modalColors.enableButtonTextColor || 'white'}
					fontWeight="semibold"
					fontSize="md"
					_hover={{
						bg: useSemanticDarkModal ? 'primary.bgHover' : modalColors.enableButtonHoverBg,
						transform: 'translateY(-1px)',
						boxShadow: useSemanticDarkModal ? 'md' : modalColors.enableButtonHoverBoxShadow,
					}}
					_active={{
						bg: useSemanticDarkModal ? 'primary.bgActive' : modalColors.enableButtonActiveBg,
						transform: 'translateY(0)',
					}}
					transition="all 0.2s"
					boxShadow={useSemanticDarkModal ? 'sm' : modalColors.enableButtonBoxShadow}
				>
					{modalText.enableButtonText} {seasonConfig.toggleEmoji}
				</Button>

				<Button
					onClick={onClose}
					size="sm"
					width="full"
					variant="ghost"
					color={useSemanticDarkModal ? 'text.interactive' : modalColors.cancelButtonColor}
					_hover={{
						bg: useSemanticDarkModal ? 'secondary.bgAlpha' : modalColors.closeButtonHoverBg || 'whiteAlpha.100',
						color: useSemanticDarkModal ? 'text.interactive' : modalColors.cancelButtonHoverColor,
					}}
				>
					{modalText.cancelButtonText}
				</Button>
			</ModalFooter>
		</ModalContent>
	);
};

/**
 * Scenic layout used by themes with rich live backgrounds (spring).
 *
 * Differences from classic:
 * - Gradient accent strip behind the logo that echoes the live sky
 * - Smaller, better-proportioned logo
 * - Feature rows use an emoji bubble instead of a full-width card
 * - Enable button supports a gradient fill with soft glow
 * - The availability notice lives in the footer as quiet helper text
 */
const ScenicModalContent: FC<VariantProps> = ({ seasonConfig, useSemanticDarkModal, onClose, onEnable }) => {
	const { modalColors, modalDecorations, logoDark, logoLight, modalText } = seasonConfig;
	const modalLogo = useSemanticDarkModal ? logoDark : modalColors.logoOverride === 'light' ? logoLight : logoDark;

	const enableBgImage = useSemanticDarkModal ? undefined : modalColors.enableButtonBgGradient;
	const enableBgHoverImage = useSemanticDarkModal ? undefined : (modalColors.enableButtonHoverBgGradient ?? modalColors.enableButtonBgGradient);
	const enableBgBase = useSemanticDarkModal ? 'primary.bg' : enableBgImage ? undefined : modalColors.enableButtonBg;
	const enableBgHoverBase = useSemanticDarkModal ? 'primary.bgHover' : enableBgHoverImage ? undefined : modalColors.enableButtonHoverBg;

	const bubbleGradient = useSemanticDarkModal ? undefined : modalColors.featureBubbleGradient;
	const bubbleBorder = useSemanticDarkModal ? 'border.soft' : (modalColors.featureBubbleBorderColor ?? modalColors.featureCardBorderColor);

	const headerAccent = useSemanticDarkModal ? undefined : modalColors.headerAccentGradient;

	return (
		<ModalContent
			bg={useSemanticDarkModal ? 'bg.surface' : modalColors.contentBg}
			bgImage={useSemanticDarkModal ? undefined : modalColors.contentBgImage}
			bgSize={useSemanticDarkModal ? undefined : modalColors.contentBgSize}
			bgPosition={useSemanticDarkModal ? undefined : modalColors.contentBgPosition}
			bgRepeat={useSemanticDarkModal ? undefined : modalColors.contentBgRepeat}
			borderRadius="2xl"
			boxShadow={useSemanticDarkModal ? 'xl' : modalColors.boxShadow}
			border="1px solid"
			borderColor={useSemanticDarkModal ? 'border.default' : modalColors.borderColor}
			overflow="hidden"
			position="relative"
		>
			{/* Header accent strip — soft gradient that echoes the live sky and bleeds down behind the logo. */}
			{headerAccent && <Box position="absolute" top={0} left={0} right={0} height="140px" background={headerAccent} pointerEvents="none" zIndex={0} />}

			{/* Decorative emojis — positioned as described in config, rendered behind content. */}
			{modalDecorations.map((decoration, index) => (
				<Box
					key={index}
					position="absolute"
					top={decoration.top}
					right={decoration.right}
					bottom={decoration.bottom}
					left={decoration.left}
					fontSize={decoration.fontSize}
					opacity={decoration.opacity}
					transform={`rotate(${decoration.rotation}deg)`}
					pointerEvents="none"
					zIndex={0}
				>
					{decoration.emoji}
				</Box>
			))}

			<ModalHeader pt={10} pb={2} px={6} position="relative" zIndex={1} borderBottomWidth={0}>
				{modalLogo && (
					<VStack spacing={3} align="center">
						<Box
							as="img"
							src={modalLogo}
							alt="Seasonal Bonusly Logo"
							maxW={modalColors.logoMaxWidth || '200px'}
							w="100%"
							h="auto"
							{...(useSemanticDarkModal || modalColors.logoDropShadowColor === 'transparent'
								? {}
								: { filter: `drop-shadow(0 4px 15px ${modalColors.logoDropShadowColor})` })}
						/>
					</VStack>
				)}
				<ModalCloseButton
					color={useSemanticDarkModal ? 'text.default' : modalColors.cancelButtonColor}
					_hover={{
						bg: useSemanticDarkModal ? 'secondary.bgAlphaHover' : modalColors.closeButtonHoverBg || 'whiteAlpha.200',
						color: useSemanticDarkModal ? 'text.default' : modalColors.cancelButtonHoverColor,
					}}
					top={4}
					right={4}
					size="lg"
				/>
			</ModalHeader>

			<ModalBody px={7} pb={2} pt={2} position="relative" zIndex={1}>
				<VStack spacing={5} align="stretch">
					<Text
						fontSize="sm"
						color={useSemanticDarkModal ? 'text.soft' : modalColors.subtitleColor}
						textAlign="center"
						lineHeight="tall"
						fontWeight="medium"
						mt={1}
						mb={1}
					>
						{modalText.subtitle}
					</Text>

					<VStack spacing={3.5} align="stretch">
						{modalText.features.map((feature, index) => (
							<HStack key={index} spacing={3.5} align="center">
								<Box
									flexShrink={0}
									w="44px"
									h="44px"
									borderRadius="full"
									display="flex"
									alignItems="center"
									justifyContent="center"
									fontSize="22px"
									background={bubbleGradient || (useSemanticDarkModal ? 'var(--chakra-colors-bg-subtle)' : modalColors.featureCardBg)}
									border="1px solid"
									borderColor={bubbleBorder ?? 'transparent'}
									boxShadow={useSemanticDarkModal ? 'none' : 'sm'}
								>
									{feature.emoji}
								</Box>
								<Text fontSize="sm" color={useSemanticDarkModal ? 'text.default' : modalColors.featureCardTextColor || 'gray.700'} lineHeight="short">
									{feature.text}
								</Text>
							</HStack>
						))}
					</VStack>
				</VStack>
			</ModalBody>

			<ModalFooter px={7} pb={6} pt={5} position="relative" zIndex={1} flexDirection="column" gap={2}>
				<Button
					onClick={onEnable}
					size="md"
					width="full"
					bg={enableBgBase}
					bgImage={enableBgImage}
					color={useSemanticDarkModal ? 'text.onColor' : modalColors.enableButtonTextColor || 'white'}
					fontWeight="semibold"
					fontSize="md"
					borderRadius="lg"
					_hover={{
						bg: enableBgHoverBase,
						bgImage: enableBgHoverImage,
						transform: 'translateY(-1px)',
						boxShadow: useSemanticDarkModal ? 'md' : modalColors.enableButtonHoverBoxShadow,
					}}
					_active={{
						bg: useSemanticDarkModal ? 'primary.bgActive' : modalColors.enableButtonActiveBg,
						bgImage: enableBgImage,
						transform: 'translateY(0)',
					}}
					transition="all 0.2s"
					boxShadow={useSemanticDarkModal ? 'sm' : modalColors.enableButtonBoxShadow}
				>
					{modalText.enableButtonText} {seasonConfig.toggleEmoji}
				</Button>

				<Button
					onClick={onClose}
					size="sm"
					width="full"
					variant="ghost"
					color={useSemanticDarkModal ? 'text.interactive' : modalColors.cancelButtonColor}
					_hover={{
						bg: useSemanticDarkModal ? 'secondary.bgAlpha' : modalColors.closeButtonHoverBg || 'whiteAlpha.100',
						color: useSemanticDarkModal ? 'text.interactive' : modalColors.cancelButtonHoverColor,
					}}
				>
					{modalText.cancelButtonText}
				</Button>

				{/* Quieter helper text instead of a separate boxed notice — keeps the body focused on the features. */}
				<Text fontSize="xs" color="text.softest" textAlign="center" mt={1} px={2}>
					{modalText.endDateNotice}
				</Text>
			</ModalFooter>
		</ModalContent>
	);
};
